import type { GridPosition } from "../models/ModuleGrid.js";
import {
  DEFAULT_SPACESHIP_ID,
  defaultSpaceship,
  initialUnlockedCellsForSpaceship,
  sanitizeSpaceshipUnlockedCells,
  spaceshipById,
} from "../data/spaceshipData.js";

export interface SpaceshipSaveState {
  readonly selectedId: string;
  readonly ownedIds: readonly string[];
  readonly moduleUnlocks: Readonly<Record<string, readonly GridPosition[]>>;
}

function uniqueValidSpaceshipIds(ids: readonly string[] | undefined, selectedId: string): string[] {
  const out: string[] = [];
  const push = (id: string): void => {
    if (!spaceshipById(id) || out.includes(id)) return;
    out.push(id);
  };
  push(DEFAULT_SPACESHIP_ID);
  push(selectedId);
  ids?.forEach(push);
  return out;
}

function cloneCells(cells: readonly GridPosition[]): GridPosition[] {
  return cells.map((cell) => ({ x: cell.x, y: cell.y }));
}

function defaultUnlocks(): Record<string, GridPosition[]> {
  return {
    [DEFAULT_SPACESHIP_ID]: initialUnlockedCellsForSpaceship(defaultSpaceship()),
  };
}

export class SpaceshipController {
  private selectedIdValue: string;
  private readonly ownedIdsValue: string[];
  private readonly moduleUnlocksByShip: Record<string, GridPosition[]>;

  constructor(state?: Partial<SpaceshipSaveState> | string) {
    const selectedRaw = typeof state === "string" ? state : state?.selectedId;
    const selectedId = selectedRaw && spaceshipById(selectedRaw) ? selectedRaw : DEFAULT_SPACESHIP_ID;
    this.selectedIdValue = selectedId;
    this.ownedIdsValue = uniqueValidSpaceshipIds(
      typeof state === "string" ? undefined : state?.ownedIds,
      selectedId,
    );
    this.moduleUnlocksByShip = defaultUnlocks();
    const rawUnlocks = typeof state === "string" ? undefined : state?.moduleUnlocks;
    if (rawUnlocks) {
      for (const [id, cells] of Object.entries(rawUnlocks)) {
        const def = spaceshipById(id);
        if (!def) continue;
        this.moduleUnlocksByShip[id] = sanitizeSpaceshipUnlockedCells(def, cells);
      }
    }
    for (const id of this.ownedIdsValue) this.ensureModuleUnlocks(id);
  }

  get selectedId(): string {
    return this.selectedIdValue;
  }

  ownedIds(): string[] {
    return [...this.ownedIdsValue];
  }

  owns(id: string): boolean {
    return this.ownedIdsValue.includes(id);
  }

  unlockSpaceship(id: string): boolean {
    if (!spaceshipById(id)) return false;
    if (!this.ownedIdsValue.includes(id)) this.ownedIdsValue.push(id);
    this.ensureModuleUnlocks(id);
    return true;
  }

  select(id: string): boolean {
    if (id === this.selectedIdValue) return false;
    if (!this.owns(id)) return false;
    this.selectedIdValue = id;
    this.ensureModuleUnlocks(id);
    return true;
  }

  moduleUnlockedCells(id: string): GridPosition[] {
    this.ensureModuleUnlocks(id);
    return cloneCells(this.moduleUnlocksByShip[id] ?? []);
  }

  setModuleUnlockedCells(id: string, cells: readonly GridPosition[]): void {
    const def = spaceshipById(id);
    if (!def) return;
    this.moduleUnlocksByShip[id] = sanitizeSpaceshipUnlockedCells(def, cells);
  }

  private ensureModuleUnlocks(id: string): void {
    const def = spaceshipById(id);
    if (!def) return;
    this.moduleUnlocksByShip[id] = sanitizeSpaceshipUnlockedCells(def, this.moduleUnlocksByShip[id]);
  }

  toState(): SpaceshipSaveState {
    const moduleUnlocks: Record<string, GridPosition[]> = {};
    for (const id of this.ownedIdsValue) moduleUnlocks[id] = this.moduleUnlockedCells(id);
    return {
      selectedId: this.selectedIdValue,
      ownedIds: this.ownedIds(),
      moduleUnlocks,
    };
  }

  static fromState(state: Partial<SpaceshipSaveState> | string | undefined): SpaceshipController {
    return new SpaceshipController(state);
  }
}
